import React, { useMemo } from 'react';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Chip,
  Typography,
  useTheme,
} from '@mui/material';

import type { ProviderSummary } from '../../types';

interface ClinicianTableRowProps {
  provider: ProviderSummary;
  onClick: (id: string, name?: string) => void;
}

// Helper to format time in seconds to days/hours
const formatTimeToSubmit = (seconds: number | null | undefined): { value: string; label: string } => {
  if (!seconds || seconds === 0) {
    return { value: '—', label: '—' };
  }

  const days = seconds / (24 * 60 * 60);
  const hours = seconds / (60 * 60);

  if (days >= 1) {
    const roundedDays = Math.round(days * 10) / 10;
    return {
      value: `${roundedDays} day${roundedDays === 1 ? '' : 's'} avg`,
      label: days <= 2 ? 'Great' : days <= 5 ? 'Good' : days <= 10 ? 'Fair' : 'Needs Improvement',
    };
  } else if (hours >= 1) {
    const roundedHours = Math.round(hours * 10) / 10;
    return {
      value: `${roundedHours} hr${roundedHours === 1 ? '' : 's'} avg`,
      label: hours <= 12 ? 'Great' : hours <= 24 ? 'Good' : 'Fair',
    };
  } else {
    const minutes = Math.round(seconds / 60);
    return {
      value: `${minutes} min avg`,
      label: 'Great',
    };
  }
};

export const ClinicianTableRow: React.FC<ClinicianTableRowProps> = ({ provider, onClick }) => {
  const theme = useTheme();

  const name = provider.provider_name || 'Unknown Clinician';
  const email = (provider as any).email || `${name.toLowerCase().replace(' ', '.')}@mockclinic.com`;

  // Use real backend data
  const unsignedCount = provider.unsigned_count ?? 0;
  const timeToSubmitData = useMemo(
    () => formatTimeToSubmit(provider.avg_time_to_submit_seconds),
    [provider.avg_time_to_submit_seconds],
  );

  // Revenue is not available in backend data, keeping as placeholder
  const revenue = "$0";
  const revenueTrend = "—";

  return (
    <Box
      onClick={() => onClick(provider.user_id, name)}
      sx={{
        display: 'grid',
        gridTemplateColumns: '48px 1.5fr 1fr 1.2fr 1fr 1fr 1.2fr 48px',
        alignItems: 'center',
        px: 2,
        py: 2,
        bgcolor: 'background.paper',
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
        cursor: 'pointer',
        '&:hover': {
          bgcolor: '#FBFBFF',
        },
      }}
    >
      <Box onClick={(e) => e.stopPropagation()}>
        <Checkbox size="small" />
      </Box>

      {/* Clinician Info */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
          {name}
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          {email}
        </Typography>
      </Box>

      {/* Notes This Period */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          {provider.notes_this_period ?? 0}
        </Typography>
      </Box>

      {/* Unsigned */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          label={`${unsignedCount} Note${unsignedCount === 1 ? '' : 's'}`}
          size="small"
          variant="outlined"
          sx={{
            height: 24,
            fontSize: '0.75rem',
            color: 'text.secondary',
            borderColor: 'divider',
          }}
        />
        {unsignedCount > 0 && (
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#6366f1' }} />
        )}
      </Box>

      {/* Time to Submit */}
      <Box>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: provider.avg_time_to_submit_seconds
              ? theme.palette.text.primary
              : theme.palette.text.disabled,
          }}
        >
          {timeToSubmitData.value}
        </Typography>
      </Box>

      {/* Compliance % */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color:
              provider.compliance_percentage !== null && provider.compliance_percentage !== undefined
                ? provider.compliance_percentage >= 80
                  ? theme.palette.success.main
                  : provider.compliance_percentage >= 60
                    ? theme.palette.warning.main
                    : theme.palette.error.main
                : theme.palette.text.disabled,
          }}
        >
          {provider.compliance_percentage !== null && provider.compliance_percentage !== undefined
            ? `${provider.compliance_percentage.toFixed(0)}%`
            : '—'}
        </Typography>
      </Box>

      {/* CPT Match % */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color:
              provider.cpt_accuracy_pct !== null && provider.cpt_accuracy_pct !== undefined
                ? provider.cpt_accuracy_pct >= 90
                  ? theme.palette.success.main
                  : provider.cpt_accuracy_pct >= 70
                    ? theme.palette.warning.main
                    : theme.palette.error.main
                : theme.palette.text.disabled,
          }}
        >
          {provider.cpt_accuracy_pct !== null && provider.cpt_accuracy_pct !== undefined
            ? `${provider.cpt_accuracy_pct.toFixed(0)}%`
            : '—'}
        </Typography>
      </Box>

      <Box>
        <ChevronRightIcon sx={{ color: theme.palette.grey[400] }} />
      </Box>
    </Box>
  );
};

export const ClinicianTableHeader: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '48px 1.5fr 1fr 1.2fr 1fr 1fr 1.2fr 48px',
        alignItems: 'center',
        px: 2,
        py: 1.5,
        bgcolor: '#F9FAFB',
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: '12px 12px 0 0',
      }}
    >
      <Box>
        <Checkbox size="small" />
      </Box>

      <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
        Clinicians
      </Typography>

      <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
        Notes This Period
      </Typography>

      <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
        Unsigned
      </Typography>

      <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
        Time to Submit
      </Typography>

      <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
        Compliance %
      </Typography>

      <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
        CPT Match %
      </Typography>

      <Box />
    </Box>
  );
};

